import { useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { FabricCanvas } from './FabricCanvas.js';
import { desplazamientoDeZona, lucesDeZona } from './luces.js';
import { necesitaUmbral, previewRgb } from '@besbello/design-core';
import type { CanonicalDocument, Color, Layer, PointMm } from '@besbello/design-core';

/**
 * EL DISENO PUESTO EN LA PRENDA.
 *
 * Hasta aqui el editor ensenaba el diseno sobre un rectangulo del color de la
 * prenda. Es lo correcto para trabajar —cada milimetro donde toca— pero no
 * responde a la pregunta que hace todo el mundo antes de pagar: «¿como va a
 * quedar?».
 *
 * Esto lo compone sobre la FOTO real del producto. Tres reglas:
 *
 *  1. Se alimenta del MISMO documento canonico que la produccion. No hay una
 *     version «de mockup» del diseno (P1).
 *  2. Es ORIENTATIVO y lo dice en pantalla. La foto es de una talla concreta,
 *     con sus pliegues y su iluminacion; el encaje se hereda de la familia
 *     hasta que alguien lo mide. Nunca sustituye a una prueba impresa.
 *  3. Si falta la foto o el encaje, no se ensena nada roto: se explica que
 *     falta calibrar y el editor sigue funcionando igual.
 *
 * La tinta se funde con `multiply` sobre prenda clara, que es lo que hace que
 * los pliegues y las sombras de la foto se vean A TRAVES del estampado. En
 * prenda oscura no se puede: multiplicar por un fondo oscuro se come el diseno,
 * asi que ahi se pinta plano.
 */

export interface EncajeMockup {
  imageUrl: string | null;
  /** Milimetros de prenda por pixel de la foto. */
  mmPerPixel: number;
  anchor: { xFrac: number; yFrac: number } | null;
  provenance: 'inherited' | 'adjusted' | 'exception';
  confidence: number;
}

export interface MockupPrendaProps {
  document: CanonicalDocument;
  zonePolygonMm: PointMm[];
  /**
   * Cuánto se aparta esta zona del ancla de su vista, en milímetros.
   *
   * El ancla apunta a la zona GRANDE de la vista —el estampado de pecho o de
   * espalda—, así que sin esto todas las zonas se dibujaban en el mismo sitio y
   * un pecho izquierdo de 90 mm salía en mitad del pecho, sólo que más pequeño.
   *
   * Lo calcula el servidor, que es quien conoce las demás zonas del patrón.
   */
  zoneOffsetMm?: { x: number; y: number };
  encaje: EncajeMockup;
  /** Fotos ya resueltas de los assets del diseno, por checksum. */
  assetUrls: Record<string, string>;
  /** Prenda oscura: cambia como se funde la tinta. */
  dark: boolean;
  className?: string;
  /**
   * Trabajar ENCIMA de la foto: arrastrar, escalar y girar sobre la prenda.
   *
   * Es donde el cliente decide de verdad. Colocar en un rectangulo plano y
   * luego comprobar en la prenda obliga a ir y venir, y cada viaje es una
   * ocasion para abandonar.
   */
  editable?: boolean;
  selectedLayerId?: string | null;
  layersWithErrors?: string[];
  /**
   * AJUSTAR EL ENCAJE arrastrando, en vez de escribiendo fracciones.
   *
   * Con 470 productos, corregir el encaje a base de números no lo hace nadie:
   * se queda heredado de la familia con un 50 % de confianza para siempre.
   * Arrastrando el recuadro sobre la propia foto se ajusta en segundos.
   *
   * Solo para quien tiene permiso de catálogo: mueve el dato de TODOS los
   * diseños de ese producto, no solo el que se está viendo.
   */
  ajustable?: boolean;
  onEncaje?: (encaje: { xFrac: number; yFrac: number; mmPerPixel: number }) => void;
  /**
   * La cara que se trabaja y la cara de la FOTO que se muestra.
   *
   * Cuando no hay foto de la espalda en el color elegido se cae a la delantera:
   * el estampado se está colocando bien (la zona es la de espalda), pero la
   * prenda que se ve es la de delante. Si las dos caras no coinciden, se avisa
   * en vez de dejar creer que así queda por detrás.
   */
  caraPedida?: string;
  caraMostrada?: string | null;
  /**
   * La TÉCNICA de estampación, para el acabado. Un bordado tiene relieve y
   * sombra, una sublimación se absorbe en la tela, una serigrafía es mate y
   * opaca, un DTF es plano. Es una capa sutil; la prueba impresa manda.
   */
  techniqueId?: string;
  /**
   * Cuánto escala el estampado con la TALLA elegida, respecto a la talla base
   * del diseño.
   *
   * Una camiseta de niño de 4 años tiene un área de marcaje ~0,61 de la M: el
   * mismo diseño sale más pequeño y más arriba. El factor lo da la zona que
   * RESUELVE EL SERVIDOR para esa talla (no se recalcula aquí, para no divergir
   * de la tabla infantil). 1 = la talla base, sin cambio.
   */
  factorTalla?: number;
}

/**
 * El acabado de cada técnica, como estilo CSS sutil sobre el estampado.
 *
 * No pretende reproducir el tacto: es una pista visual para que un bordado no
 * se vea igual de plano que un DTF. La verdad la da la prueba impresa.
 *  - bordado: relieve y sombra propia, hilo algo menos saturado.
 *  - sublimación: la tinta se mete en la tela, así que se aclara y deja pasar
 *    algo de tejido (menos opaca).
 *  - serigrafía: tinta mate y opaca, un punto más saturada.
 *  - DTF: plano, sin adorno (es lo que es).
 */
function estiloDeAcabado(tecnica: string | undefined): { filter?: string; opacity?: number } {
  switch (tecnica) {
    case 'embroidery':
      return { filter: 'saturate(0.9) contrast(1.04) drop-shadow(0.4px 0.8px 0.4px rgba(0,0,0,0.42))' };
    case 'sublimation':
      return { filter: 'saturate(0.95)', opacity: 0.9 };
    case 'screen_print':
      return { filter: 'saturate(1.05)' };
    default:
      return {};
  }
}

/** Código de vista → nombre de la cara, para el aviso. */
function nombreCara(code: string): string {
  return (
    { front: 'la delantera', back: 'la espalda', left: 'el lateral', right: 'el lateral', wrap: 'el contorno' }[
      code
    ] ?? code
  );
}

function limites(polygon: PointMm[]): { minX: number; minY: number; widthMm: number; heightMm: number } {
  const xs = polygon.map((p) => p.x);
  const ys = polygon.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { minX, minY, widthMm: Math.max(...xs) - minX, heightMm: Math.max(...ys) - minY };
}

export function MockupPrenda(props: MockupPrendaProps) {
  const { encaje, zonePolygonMm, document: doc } = props;
  const contenedor = useRef<HTMLDivElement>(null);
  const [anchoPx, setAnchoPx] = useState(0);
  /*
    El ancho REAL de la foto en píxeles, medido al cargarla (`naturalWidth`), no
    supuesto. `mmPerPixel` se calibró contra esa misma resolución de origen, así
    que reconstruir la escala con la cifra medida —en vez de un 600 grabado a
    fuego en tres sitios— hace que la colocación no se descuadre en silencio si
    el proveedor cambia el tamaño de sus fotos. 600 es solo el valor mientras
    carga: las de Roly son cuadradas de 600.
  */
  const [anchoFotoPx, setAnchoFotoPx] = useState(600);
  /** Arrastre del recuadro en modo ajuste: desde dónde se agarró. */
  const arrastre = useRef<{ x: number; y: number; xFrac: number; yFrac: number } | null>(null);

  // El mockup se dibuja en fracciones de la foto, asi que necesita saber a
  // cuantos pixeles se esta pintando esa foto AHORA.
  useEffect(() => {
    const el = contenedor.current;
    if (!el) return;
    const medir = () => setAnchoPx(el.getBoundingClientRect().width);
    medir();
    const observer = new ResizeObserver(medir);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!encaje.imageUrl || !encaje.anchor) {
    return (
      <div className={`rounded-lg border border-dashed border-borde p-6 text-center ${props.className ?? ''}`}>
        <p className="text-sm text-tinta-suave">
          {!encaje.imageUrl
            ? 'Este producto todavía no tiene foto para la vista previa.'
            : 'Falta calibrar dónde cae el estampado en la foto de esta prenda.'}
        </p>
      </div>
    );
  }

  const zona = limites(zonePolygonMm);

  /*
    De milimetros a fraccion de la foto.

    `mmPerPixel` se midio sobre la imagen ORIGINAL, no sobre el tamano al que se
    esta pintando ahora. Trabajando en fracciones el mockup vale igual en una
    miniatura que a pantalla completa, que es justo lo que hace falta para que
    se vea bien en un movil y en un ordenador. El ancho de origen se MIDE al
    cargar la foto (`anchoFotoPx`), no se supone.
  */
  const fraccionPorMm = 1 / (encaje.mmPerPixel * anchoFotoPx);

  /*
    El rectángulo del estampado sobre la foto escala con la talla: `zona` (el
    espacio de milímetros donde se colocan las capas) NO cambia —el diseño se
    autoría una vez— pero su caja sobre la prenda se encoge o crece con el
    factor de la talla, y el diseño la llena, así que sale a escala.
  */
  const factorTalla = props.factorTalla ?? 1;
  const zonaAnchoFrac = zona.widthMm * fraccionPorMm * factorTalla;
  const zonaAltoFrac = zona.heightMm * fraccionPorMm * factorTalla;

  /*
    El ancla apunta a la zona GRANDE de esta vista, y cada zona se coloca a
    partir de ahí con su propio desplazamiento. Para la zona grande vale cero y
    sale exactamente donde salía; para un pecho izquierdo, a un lado, que es
    donde va.
  */
  const desplazamiento = props.zoneOffsetMm ?? { x: 0, y: 0 };
  const zonaIzquierdaFrac = encaje.anchor.xFrac + desplazamiento.x * fraccionPorMm - zonaAnchoFrac / 2;
  const zonaArribaFrac = encaje.anchor.yFrac + desplazamiento.y * fraccionPorMm;

  // El recorte de la foto que cae bajo la zona: lo comparten las luces y los
  // pliegues, que salen del mismo trozo de foto.
  const recorteZona = {
    izquierdaFrac: zonaIzquierdaFrac,
    arribaFrac: zonaArribaFrac,
    anchoFrac: zonaAnchoFrac,
    altoFrac: zonaAltoFrac,
  };

  const capas = [...doc.layers].sort((a, b) => a.z - b.z);

  return (
    <div className={props.className}>
      <div ref={contenedor} className="relative overflow-hidden rounded-lg bg-white">
        <img
          src={encaje.imageUrl}
          alt=""
          className="block w-full"
          onLoad={(e) => setAnchoFotoPx(e.currentTarget.naturalWidth || 600)}
        />

        {/* La zona de marcaje: todo lo que se salga de aqui no se imprime. */}
        <div
          className={`absolute overflow-hidden ${props.ajustable ? 'cursor-move ring-2 ring-marca' : ''}`}
          {...(props.ajustable
            ? {
                'data-test': 'encaje-arrastrable',
                onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
                  const caja = contenedor.current?.getBoundingClientRect();
                  if (!caja || !encaje.anchor) return;
                  e.currentTarget.setPointerCapture(e.pointerId);
                  arrastre.current = {
                    x: e.clientX,
                    y: e.clientY,
                    xFrac: encaje.anchor.xFrac,
                    yFrac: encaje.anchor.yFrac,
                  };
                },
                onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
                  const inicio = arrastre.current;
                  const caja = contenedor.current?.getBoundingClientRect();
                  if (!inicio || !caja) return;
                  // El desplazamiento va en FRACCIÓN de la foto, no en píxeles:
                  // así el ajuste vale igual hecho en un móvil que en un monitor.
                  props.onEncaje?.({
                    xFrac: Math.min(1, Math.max(0, inicio.xFrac + (e.clientX - inicio.x) / caja.width)),
                    yFrac: Math.min(1, Math.max(0, inicio.yFrac + (e.clientY - inicio.y) / caja.height)),
                    mmPerPixel: encaje.mmPerPixel,
                  });
                },
                onPointerUp: () => {
                  arrastre.current = null;
                },
              }
            : {})}
          style={{
            left: `${zonaIzquierdaFrac * 100}%`,
            top: `${zonaArribaFrac * 100}%`,
            width: `${zonaAnchoFrac * 100}%`,
            height: `${zonaAltoFrac * 100}%`,
            /*
              Sobre prenda clara la tinta deja pasar los pliegues de la foto;
              sobre oscura, multiplicar borraría el diseño.

              Mientras se edita se pinta PLANO aunque la prenda sea clara: con
              `multiply`, los tiradores de la selección se funden con la foto y
              dejan de verse justo cuando hacen falta. La fusión vuelve en
              cuanto se sale del modo de edición, que es cuando importa.
            */
            mixBlendMode: props.dark || props.editable ? 'normal' : 'multiply',
          }}
        >
          {/*
            EDITABLE: el MISMO lienzo de trabajo, sin fondo, encima de la foto.

            No es un segundo editor ni una copia del dibujo: es el mismo
            componente sobre el mismo documento. Cualquier cosa que se arregle
            en el lienzo de trabajo queda arreglada aquí, y no hay forma de que
            las dos vistas se separen.
          */}
          {props.editable ? (
            <FabricCanvas
              document={doc}
              zonePolygonMm={zonePolygonMm}
              assetUrls={props.assetUrls}
              garmentRgb={{ r: 255, g: 255, b: 255 }}
              selectedLayerId={props.selectedLayerId ?? null}
              layersWithErrors={props.layersWithErrors ?? []}
              transparente
            />
          ) : (
            anchoPx > 0 && (
              <>
                {/*
                  EL ESTAMPADO, DEFORMADO POR LOS PLIEGUES.

                  Antes las capas se pintaban planas, estiradas sobre un cuerpo
                  tridimensional como una pegatina. `DeformarPorPliegues` las
                  envuelve en un `feDisplacementMap` cuyo mapa sale del relieve
                  de la propia foto (`luces.ts`): el estampado se dobla siguiendo
                  la tela. Si la foto no se puede leer, no envuelve nada y queda
                  plano, como estaba.
                */}
                {/* El acabado de la técnica envuelve el estampado ya deformado:
                    el bordado con su sombra, la sublimación absorbida, etc. */}
                <div className="absolute inset-0" style={estiloDeAcabado(props.techniqueId)}>
                  <DeformarPorPliegues
                    fotoUrl={encaje.imageUrl}
                    recorte={recorteZona}
                    anchoZonaPx={anchoPx * zonaAnchoFrac}
                  >
                    {capas.map((capa) => (
                      <CapaPintada
                        key={capa.id}
                        capa={capa}
                        zona={zona}
                        assetUrls={props.assetUrls}
                        anchoZonaPx={anchoPx * zonaAnchoFrac}
                      />
                    ))}
                  </DeformarPorPliegues>
                </div>
                {/*
                  PRENDA OSCURA: las LUCES de la foto encima del diseño.

                  Con clara, el `multiply` del contenedor ya deja pasar los
                  pliegues. Con oscura no se puede multiplicar —se comería el
                  diseño— así que se pintaba plano, como una pegatina. Esto
                  extrae las altas luces del recorte de foto que cae bajo la
                  zona y las funde con `screen`: la tela vuelve a pisar la
                  tinta. Van SIN deformar: son los pliegues reales de la foto, en
                  su sitio. En edición no se pinta, igual que se apaga el
                  multiply: los tiradores tienen que verse.
                */}
                {props.dark && capas.length > 0 && (
                  <LucesPliegues fotoUrl={encaje.imageUrl} recorte={recorteZona} />
                )}
              </>
            )
          )}
        </div>
      </div>

      {/*
        LA CARA EQUIVOCADA, dicha en voz alta. Si se trabaja la espalda pero el
        color no tiene foto trasera, se ve la delantera: hay que avisar, no
        dejar creer que el estampado va a salir así por detrás.
      */}
      {props.caraPedida && props.caraMostrada && props.caraPedida !== props.caraMostrada && (
        <p
          data-test="mockup-cara-distinta"
          className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900"
        >
          Este color no tiene foto de {nombreCara(props.caraPedida)}: el estampado se coloca bien, pero se ve sobre{' '}
          {nombreCara(props.caraMostrada)}.
        </p>
      )}

      <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-tinta-suave">
        <span>Vista previa orientativa.</span>
        <span>
          {encaje.provenance === 'adjusted'
            ? 'Encaje medido en esta prenda.'
            : `Encaje heredado de la familia · confianza ${Math.round(encaje.confidence * 100)}%.`}
        </span>
        <span>No sustituye a una prueba impresa.</span>
      </p>
    </div>
  );
}

/**
 * DEFORMAR EL ESTAMPADO SIGUIENDO LOS PLIEGUES de la foto.
 *
 * Envuelve las capas del diseño en un filtro `feDisplacementMap` cuyo mapa de
 * desplazamiento sale del relieve de la foto (`desplazamientoDeZona`, en
 * `luces.ts`): donde la tela es plana no mueve nada; en un pliegue, dobla el
 * estampado hacia el relieve. Es lo que saca al diseño del plano sin fingir una
 * malla 3D.
 *
 * Mientras carga el mapa —o si la foto no se puede leer— NO envuelve: pinta las
 * capas planas, exactamente como antes. La deformación es una mejora que nunca
 * bloquea el mockup.
 */
function DeformarPorPliegues({
  fotoUrl,
  recorte,
  anchoZonaPx,
  children,
}: {
  fotoUrl: string | null;
  recorte: { izquierdaFrac: number; arribaFrac: number; anchoFrac: number; altoFrac: number };
  anchoZonaPx: number;
  children: ReactNode;
}) {
  const [mapaUrl, setMapaUrl] = useState<string | null>(null);
  // `useId` trae dos puntos (":r0:"), que no valen dentro de `url(#…)`.
  const filtroId = `pliegues-${useId().replace(/:/g, '')}`;
  const claveRecorte = `${recorte.izquierdaFrac.toFixed(4)},${recorte.arribaFrac.toFixed(4)},${recorte.anchoFrac.toFixed(4)},${recorte.altoFrac.toFixed(4)}`;

  useEffect(() => {
    if (!fotoUrl) return;
    let vivo = true;
    void desplazamientoDeZona(fotoUrl, recorte).then((url) => {
      if (vivo) setMapaUrl(url);
    });
    return () => {
      vivo = false;
    };
    // `claveRecorte` resume el recorte: el objeto es nuevo en cada render.
  }, [fotoUrl, claveRecorte]);

  if (!mapaUrl) return <>{children}</>;

  // Cuánto puede empujar un pliegue, en píxeles de pantalla. Proporcional al
  // tamaño de la zona para que se note igual en móvil y en monitor, y acotado
  // para que no se retuerza.
  const escala = Math.max(3, Math.min(24, anchoZonaPx * 0.045));

  return (
    <>
      <div className="absolute inset-0" style={{ filter: `url(#${filtroId})` }}>
        {children}
      </div>
      <svg width="0" height="0" aria-hidden className="absolute" style={{ width: 0, height: 0 }}>
        <filter id={filtroId} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
          <feImage href={mapaUrl} result="mapa" x="0" y="0" width="100%" height="100%" preserveAspectRatio="none" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="mapa"
            scale={escala}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>
    </>
  );
}

/**
 * Las luces de los pliegues, fundidas con `screen` sobre el diseño.
 *
 * El trabajo caro (leer la foto, recortar, aplicar la curva) vive en
 * `luces.ts` con caché por foto y recorte; esto solo pinta el resultado.
 */
function LucesPliegues({
  fotoUrl,
  recorte,
}: {
  fotoUrl: string | null;
  recorte: { izquierdaFrac: number; arribaFrac: number; anchoFrac: number; altoFrac: number };
}) {
  const [url, setUrl] = useState<string | null>(null);
  const claveRecorte = `${recorte.izquierdaFrac.toFixed(4)},${recorte.arribaFrac.toFixed(4)},${recorte.anchoFrac.toFixed(4)},${recorte.altoFrac.toFixed(4)}`;

  useEffect(() => {
    if (!fotoUrl) return;
    let vivo = true;
    void lucesDeZona(fotoUrl, recorte).then((resultado) => {
      if (vivo) setUrl(resultado);
    });
    return () => {
      vivo = false;
    };
    // `claveRecorte` resume el contenido del recorte: el objeto es nuevo en
    // cada render y depender de su identidad relanzaría la extracción.
  }, [fotoUrl, claveRecorte]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt=""
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ mixBlendMode: 'screen' }}
    />
  );
}

/**
 * Una capa, colocada dentro de la zona.
 *
 * Todo se calcula en PORCENTAJE de la zona: asi el navegador escala solo cuando
 * cambia el tamano de la ventana y no hay que volver a pintar nada.
 */
function CapaPintada({
  capa,
  zona,
  assetUrls,
  anchoZonaPx,
}: {
  capa: Layer;
  zona: { minX: number; minY: number; widthMm: number; heightMm: number };
  assetUrls: Record<string, string>;
  anchoZonaPx: number;
}) {
  const pctX = (mm: number) => ((mm - zona.minX) / zona.widthMm) * 100;
  const pctY = (mm: number) => ((mm - zona.minY) / zona.heightMm) * 100;

  const t = capa.transform;
  const rotacion = Math.atan2(t.b, t.a) * (180 / Math.PI);

  const comun = {
    position: 'absolute' as const,
    left: `${pctX(t.e)}%`,
    top: `${pctY(t.f)}%`,
    opacity: capa.opacity,
    transform: rotacion ? `rotate(${rotacion}deg)` : undefined,
    transformOrigin: 'top left',
  };

  if (capa.kind === 'image') {
    const url = assetUrls[capa.assetChecksum];
    if (!url) return null;
    // En una capa de imagen la matriz lleva el TAMANO en milimetros (D-I-01).
    const anchoMm = Math.hypot(t.a, t.b);
    const altoMm = Math.hypot(t.c, t.d);
    return (
      <img
        src={url}
        alt=""
        style={{
          ...comun,
          width: `${(anchoMm / zona.widthMm) * 100}%`,
          height: `${(altoMm / zona.heightMm) * 100}%`,
          objectFit: 'fill',
          /*
            Los mismos ajustes que en el lienzo, con los filtros de CSS. Aquí no
            se puede hacer el corte por umbral —CSS no lo tiene— así que se
            aproxima con un contraste extremo. Es una vista previa; la que manda
            es la del lienzo, y la definitiva la del compositor.
          */
          filter: filtroCss(capa.ajustes),
          // El recorte se hace con la caja: se agranda la imagen y se enseña
          // solo la parte pedida.
          ...(capa.recorte
            ? {
                objectFit: 'none' as const,
                objectPosition: `${capa.recorte.xFrac * 100}% ${capa.recorte.yFrac * 100}%`,
              }
            : {}),
        }}
      />
    );
  }

  if (capa.kind === 'text') {
    const pxPorMm = anchoZonaPx / zona.widthMm;
    return (
      <span
        style={{
          ...comun,
          // El tamano del texto va en milimetros; se pasa a pixeles con la
          // escala a la que se este pintando la zona ahora mismo.
          fontSize: `${capa.sizeMm * pxPorMm}px`,
          fontFamily: capa.font.family,
          fontWeight: capa.font.weight,
          fontStyle: capa.font.italic ? 'italic' : 'normal',
          letterSpacing: `${capa.tracking}em`,
          lineHeight: capa.lineHeightMm / capa.sizeMm,
          textAlign: capa.align,
          textTransform:
            capa.textCase === 'upper' ? 'uppercase' : capa.textCase === 'lower' ? 'lowercase' : 'none',
          color: cssColor(capa.color),
          // Contorno: `paint-order` pinta el trazo por DEBAJO del relleno, que es
          // como sale impreso. Sin el, el contorno se come la mitad de la letra.
          WebkitTextStrokeWidth: capa.outline ? `${capa.outline.widthMm * pxPorMm}px` : undefined,
          WebkitTextStrokeColor: capa.outline ? cssColor(capa.outline.color) : undefined,
          paintOrder: capa.outline ? 'stroke fill' : undefined,
          whiteSpace: 'pre',
        }}
      >
        {capa.text}
      </span>
    );
  }

  // Forma: el trazado viene en milimetros, se dibuja en su propio SVG.
  const anchoMm = Math.hypot(t.a, t.b) || zona.widthMm;
  const altoMm = Math.hypot(t.c, t.d) || zona.heightMm;
  return (
    <svg
      viewBox={`0 0 ${zona.widthMm} ${zona.heightMm}`}
      style={{
        ...comun,
        width: `${(anchoMm / zona.widthMm) * 100}%`,
        height: `${(altoMm / zona.heightMm) * 100}%`,
        overflow: 'visible',
      }}
    >
      <path
        d={capa.path}
        fill={capa.fill ? cssColor(capa.fill) : 'none'}
        stroke={capa.stroke ? cssColor(capa.stroke) : undefined}
        strokeWidth={capa.strokeWidthMm ?? 0}
      />
    </svg>
  );
}

/**
 * El MISMO paso a color de pantalla que usa el lienzo de trabajo.
 *
 * `previewRgb` vive en design-core y sabe convertir las cuatro clases de color
 * del documento: rgb, spot, blanco tecnico y cmyk. Escribir aqui otra version
 * seria garantizar que un dia el mockup y el editor ensenan colores distintos.
 */
function cssColor(color: Color): string {
  const { r, g, b } = previewRgb(color);
  return `rgb(${r},${g},${b})`;
}

/**
 * Los ajustes de imagen traducidos a `filter` de CSS.
 *
 * El mockup es una vista previa rápida y aquí lo que hay es CSS, no un lienzo.
 * El corte por umbral no existe en CSS, así que los dos modos duros se
 * aproximan con un contraste extremo: se parece lo suficiente para decidir, y
 * la versión que manda es la del lienzo.
 */
function filtroCss(ajustes: import('@besbello/design-core').AjustesImagen | undefined): string | undefined {
  if (!ajustes) return undefined;
  const partes: string[] = [];
  if (ajustes.brillo !== 1) partes.push(`brightness(${ajustes.brillo})`);
  if (ajustes.contraste !== 1) partes.push(`contrast(${ajustes.contraste})`);

  if (ajustes.modo === 'grises') {
    partes.push('grayscale(1)');
  } else if (necesitaUmbral(ajustes)) {
    partes.push('grayscale(1)');
    partes.push(`brightness(${(1 - ajustes.umbral) * 2})`);
    partes.push('contrast(20)');
  } else if (ajustes.saturacion !== 1) {
    partes.push(`saturate(${ajustes.saturacion})`);
  }

  return partes.length > 0 ? partes.join(' ') : undefined;
}
